import { Session, sessionContentAreEquals, sessionDeepCopy } from "@domain/program/session";
import { isNil } from "@utils/assert";
import { getEnumValues } from "@utils/enum-utils";

export enum DayIndex {
	MONDAY = 1,
	TUESDAY = 2,
	WEDNESDAY = 3,
	THURSDAY = 4,
	FRIDAY = 5,
	SATURDAY = 6,
	SUNDAY = 7,
}

export const dayIndexes = getEnumValues(DayIndex);

export class ProgramDays {
	days: Map<DayIndex, Session>;

	constructor(days: [DayIndex, Session][] = []) {
		this.days = new Map(dayIndexes.map(dayIndex => [dayIndex, { exercises: [] }]));
		days.forEach(([key, value]) => this.days.set(key, value));
	}

	get(dayIndex: DayIndex): Session {
		const result = this.days.get(dayIndex);
		if (isNil(result)) {
			throw new Error("Invalid dayIndex");
		}
		return result;
	}

	set(dayIndex: DayIndex, value: Session): void {
		this.days.set(dayIndex, value);
	}

	forEach(callbackfn: (value: [DayIndex, Session], index: number) => void): void {
		[...this.days.entries()].forEach((value, index) => callbackfn(value, index));
	}

	map<T>(callbackfn: (value: [DayIndex, Session], index: number) => T): T[] {
		return [...this.days.entries()].map((value, index) => callbackfn(value, index));
	}

	equals(other: ProgramDays): boolean {
		return [...this.days.entries()].every(([dayIndex, session]) =>
			sessionContentAreEquals(session, other.get(dayIndex)),
		);
	}

	copy(): ProgramDays {
		return new ProgramDays(this.map(([dayIndex, session]) => [dayIndex, sessionDeepCopy(session)]));
	}

	get exercisesPerWeek(): number {
		return [...this.days.values()].reduce((acc, session) => acc + session.exercises.length, 0);
	}

	get sessionsPerWeek(): number {
		return [...this.days.values()].reduce((acc, session) => acc + (session.exercises.length > 0 ? 1 : 0), 0);
	}

	get isEmpty(): boolean {
		return [...this.days.values()].every(session => session.exercises.length === 0);
	}

	get entries(): [DayIndex, Session][] {
		return [...this.days.entries()];
	}
}
